import React, { useState, useEffect } from 'react';
import { Bookmark, Mail } from 'lucide-react';
import WhatsAppWidget from './WhatsAppWidget';
import { FacebookIcon, InstagramIcon } from './Icons';
import useTitle from '../hooks/useTitle';
import { useTranslation } from '../hooks/useTranslation';
import { LANGUAGES } from '../types';
import ElephantLogo from './Logo';
import { CountryFlag } from './FlagIcon';

interface LayoutProps {
	children: React.ReactNode;
	activeTab: string;
	setActiveTab: (tab: string) => void;
	savedCount: number;
	onOpenBookingHistory: () => void;
	hasBookings: boolean;
	onOpenLegal?: (doc: 'terms' | 'privacy' | 'indemnity') => void;
}

export default function Layout({
	children,
	activeTab,
	setActiveTab,
	savedCount,
	onOpenBookingHistory,
	hasBookings,
	onOpenLegal,
}: LayoutProps) {
	const [isScrolled, setIsScrolled] = useState(false);
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const { currentLanguage, setLanguage, t } = useTranslation();

	const titleMap: Record<string, string> = {
		discover: t('homeTab') + ' | ' + t('curatedSafaris'),
		expeditions: t('safarisTab'),
		story: t('storyTab'),
		contact: t('contactTab'),
		saved: t('savedSafaris'),
		builder: t('requestAQuote'),
	};
	useTitle(titleMap[activeTab] || 'Elephant Expeditions');

	// Track window scroll to handle header morphing animation
	useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > 40);
		};
		window.addEventListener('scroll', handleScroll, { passive: true });
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	// Determine if the header should look transparent (only on Home/discover view when not scrolled)
	const isOverHero = activeTab === 'discover';
	const isHeaderTransparent = isOverHero && !isScrolled;

	return (
		<div id='layout-wrapper' className='min-h-screen bg-earth-cream flex flex-col justify-between'>
			{/* 1. RESPONSIVE NAVIGATION BAR (FIXED & SCROLL-MORPHED) */}
			<nav
				id='layout-navbar'
				className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
					isHeaderTransparent
						? 'bg-transparent border-transparent pt-6 pb-6 text-white'
						: 'bg-earth-cream/90 backdrop-blur-md border-b border-earth-sand/30 py-4 shadow-sm text-brand-forest'
				}`}
			>
				<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
					<div className='flex justify-between items-center h-16'>
						{/* Elegant Logo */}
						<ElephantLogo
							isHeaderTransparent={isHeaderTransparent}
							onClick={() => {
								setActiveTab('discover');
								window.scrollTo({ top: 0, behavior: 'smooth' });
							}}
						/>

						{/* Links for: Home, Safaris & Packages, Our Story, Guides */}
						<div id='layout-desktop-links' className='hidden md:flex items-center space-x-8 lg:space-x-10'>
							<button
								id='link-home'
								onClick={() => {
									setActiveTab('discover');
									window.scrollTo({ top: 0, behavior: 'smooth' });
								}}
								className={`font-sans text-xs tracking-widest uppercase font-semibold transition-all duration-200 cursor-pointer pb-1 border-b-2 ${
									activeTab === 'discover'
										? isHeaderTransparent
											? 'text-brand-gold border-brand-gold'
											: 'text-brand-forest border-brand-gold'
										: isHeaderTransparent
											? 'text-earth-sand-light/80 border-transparent hover:text-white hover:border-white/30'
											: 'text-brand-charcoal-light border-transparent hover:text-brand-forest hover:border-earth-sand'
								}`}
							>
								{t('homeTab')}
							</button>
							<button
								id='link-safaris'
								onClick={() => {
									setActiveTab('expeditions');
									window.scrollTo({ top: 0, behavior: 'smooth' });
								}}
								className={`font-sans text-xs tracking-widest uppercase font-semibold transition-all duration-200 cursor-pointer pb-1 border-b-2 ${
									activeTab === 'expeditions'
										? isHeaderTransparent
											? 'text-brand-gold border-brand-gold'
											: 'text-brand-forest border-brand-gold'
										: isHeaderTransparent
											? 'text-earth-sand-light/80 border-transparent hover:text-white hover:border-white/30'
											: 'text-brand-charcoal-light border-transparent hover:text-brand-forest hover:border-earth-sand'
								}`}
							>
								{t('safarisTab')}
							</button>
							<button
								id='link-story'
								onClick={() => {
									setActiveTab('story');
									window.scrollTo({ top: 0, behavior: 'smooth' });
								}}
								className={`font-sans text-xs tracking-widest uppercase font-semibold transition-all duration-200 cursor-pointer pb-1 border-b-2 ${
									activeTab === 'story'
										? isHeaderTransparent
											? 'text-brand-gold border-brand-gold'
											: 'text-brand-forest border-brand-gold'
										: isHeaderTransparent
											? 'text-earth-sand-light/80 border-transparent hover:text-white hover:border-white/30'
											: 'text-brand-charcoal-light border-transparent hover:text-brand-forest hover:border-earth-sand'
								}`}
							>
								{t('storyTab')}
							</button>
							<button
								id='link-contact'
								onClick={() => {
									setActiveTab('contact');
									window.scrollTo({ top: 0, behavior: 'smooth' });
								}}
								className={`font-sans text-xs tracking-widest uppercase font-semibold transition-all duration-200 cursor-pointer pb-1 border-b-2 ${
									activeTab === 'contact'
										? isHeaderTransparent
											? 'text-brand-gold border-brand-gold'
											: 'text-brand-forest border-brand-gold'
										: isHeaderTransparent
											? 'text-earth-sand-light/80 border-transparent hover:text-white hover:border-white/30'
											: 'text-brand-charcoal-light border-transparent hover:text-brand-forest hover:border-earth-sand'
								}`}
							>
								{t('contactTab')}
							</button>
						</div>

						{/* Quick indicators + CTA trigger */}
						<div id='layout-desktop-actions' className='hidden md:flex items-center space-x-6'>
							{/* Direct Language Flag Row (Always Visible) */}
							<div
								id='layout-language-flags'
								className={`flex items-center space-x-1 px-1.5 py-1 rounded-full border transition-all duration-300 ${
									isHeaderTransparent
										? 'bg-white/10 border-white/15'
										: 'bg-white/45 backdrop-blur-xs border-earth-sand/20'
								}`}
							>
								{LANGUAGES.map(lang => (
									<button
										key={lang.code}
										onClick={() => setLanguage(lang.code)}
										className={`w-7.5 h-7.5 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer border overflow-hidden p-0.5 ${
											currentLanguage === lang.code
												? 'bg-brand-forest border-brand-gold shadow-xs scale-105 ring-1 ring-brand-gold/60 z-10'
												: `border-transparent opacity-80 hover:opacity-100 hover:scale-105 ${
														isHeaderTransparent ? 'hover:bg-white/20' : 'hover:bg-earth-sand/30'
													}`
										}`}
										title={lang.name}
										aria-label={lang.name}
									>
										<CountryFlag code={lang.code} className='w-4.5 h-3.5 rounded-xs object-cover shadow-2xs' />
									</button>
								))}
							</div>

							{/* Requested Quotes / Inquiries badge */}
							{hasBookings && (
								<button
									id='layout-indicator-inquiries'
									onClick={onOpenBookingHistory}
									className={`p-1.5 flex items-center space-x-1 uppercase tracking-wider text-[10px] font-bold cursor-pointer transition-colors duration-300 ${
										isHeaderTransparent
											? 'text-brand-gold-light hover:text-white'
											: 'text-brand-terracotta hover:text-brand-forest'
									}`}
									title={t('inquiries')}
								>
									<span className='relative flex h-2.5 w-2.5'>
										<span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-terracotta opacity-75'></span>
										<span className='relative inline-flex rounded-full h-2.5 w-2.5 bg-brand-terracotta'></span>
									</span>
									<span>{t('inquiries')}</span>
								</button>
							)}

							{/* Saved list heart/bookmark icon */}
							<button
								id='layout-saved-btn'
								onClick={() => setActiveTab('saved')}
								className={`p-2 transition-colors duration-300 cursor-pointer relative rounded-full ${
									isHeaderTransparent ? 'hover:bg-white/10 text-white' : 'hover:bg-earth-sand/25 text-brand-forest'
								}`}
								title={t('savedSafaris') || 'Saved Safaris'}
								aria-label={t('savedSafaris') || 'Saved Safaris'}
							>
								<Bookmark className={`w-4.5 h-4.5 ${savedCount > 0 ? 'fill-brand-gold text-brand-gold' : ''}`} />
								{savedCount > 0 && (
									<span className='absolute -top-1 -right-1 bg-brand-terracotta text-white font-sans text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center leading-none'>
										{savedCount}
									</span>
								)}
							</button>

							{/* Prominent CTA - Request a Quote */}
							<button
								id='layout-cta-request-quote'
								onClick={() => {
									setActiveTab('builder');
									window.scrollTo({ top: 0, behavior: 'smooth' });
								}}
								className={`px-5 py-2.5 rounded-lg text-xs tracking-widest uppercase font-bold transition-all duration-300 active:scale-95 cursor-pointer ${
									isHeaderTransparent
										? 'bg-brand-gold hover:bg-white text-brand-forest-dark shadow-md border border-brand-gold'
										: 'bg-brand-forest hover:bg-brand-forest-light text-earth-cream border border-brand-forest'
								}`}
							>
								{t('requestAQuote')}
							</button>
						</div>

						{/* Mobile Hamburger Indicator Toggle Button */}
						<div className='md:hidden flex items-center space-x-3'>
							<button
								onClick={() => setActiveTab('saved')}
								className={`p-1.5 relative transition-all ${isHeaderTransparent ? 'text-white' : 'text-brand-forest'}`}
								aria-label={t('savedSafaris') || 'Saved Safaris'}
								title={t('savedSafaris') || 'Saved Safaris'}
							>
								<Bookmark className={`w-4.5 h-4.5 ${savedCount > 0 ? 'fill-brand-gold text-brand-gold' : ''}`} />
								{savedCount > 0 && (
									<span className='absolute -top-0.5 -right-0.5 bg-brand-terracotta text-white font-sans text-[8px] font-bold w-3 h-3 rounded-full flex items-center justify-center leading-none'>
										{savedCount}
									</span>
								)}
							</button>

							<button
								onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
								className={`p-1.5 focus:outline-none ${isHeaderTransparent ? 'text-white' : 'text-brand-forest'}`}
								aria-label='Toggle Menu'
							>
								<svg className='w-6 h-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
									{isMobileMenuOpen ? (
										<path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M6 18L18 6M6 6l12 12' />
									) : (
										<path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M4 6h16M4 12h16M4 18h16' />
									)}
								</svg>
							</button>
						</div>
					</div>
				</div>

				{/* Mobile Navigation Dropdown view */}
				{isMobileMenuOpen && (
					<div className='md:hidden bg-earth-cream border-b border-earth-sand/30 shadow-lg animate-fade-in absolute top-full left-0 right-0 py-4 px-6 space-y-3.5 text-left text-brand-forest'>
						<button
							onClick={() => {
								setActiveTab('discover');
								setIsMobileMenuOpen(false);
							}}
							className={`block w-full text-left font-sans text-xs tracking-widest uppercase font-semibold py-1.5 ${activeTab === 'discover' ? 'text-brand-gold font-bold' : ''}`}
						>
							{t('homeTab')}
						</button>
						<button
							onClick={() => {
								setActiveTab('expeditions');
								setIsMobileMenuOpen(false);
							}}
							className={`block w-full text-left font-sans text-xs tracking-widest uppercase font-semibold py-1.5 ${activeTab === 'expeditions' ? 'text-brand-gold font-bold' : ''}`}
						>
							{t('safarisTab')}
						</button>
						<button
							onClick={() => {
								setActiveTab('story');
								setIsMobileMenuOpen(false);
							}}
							className={`block w-full text-left font-sans text-xs tracking-widest uppercase font-semibold py-1.5 ${activeTab === 'story' ? 'text-brand-gold font-bold' : ''}`}
						>
							{t('storyTab')}
						</button>
						<button
							onClick={() => {
								setActiveTab('contact');
								setIsMobileMenuOpen(false);
							}}
							className={`block w-full text-left font-sans text-xs tracking-widest uppercase font-semibold py-1.5 ${activeTab === 'contact' ? 'text-brand-gold font-bold' : ''}`}
						>
							{t('contactTab')}
						</button>
						{hasBookings && (
							<button
								onClick={() => {
									setIsMobileMenuOpen(false);
									onOpenBookingHistory();
								}}
								className='block w-full text-left font-sans text-xs tracking-widest uppercase font-semibold py-1.5 text-brand-terracotta'
							>
								{t('inquiries')} ({savedCount})
							</button>
						)}

						{/* Mobile Language Selector Flags */}
						<div className='pt-2.5 pb-1 border-t border-earth-sand/20 space-y-2'>
							<span className='font-sans text-[9px] tracking-wider text-brand-gold-dark font-extrabold uppercase block'>
								Language / Sprache
							</span>
							<div className='flex items-center space-x-3'>
								{LANGUAGES.map(lang => (
									<button
										key={lang.code}
										onClick={() => {
											setLanguage(lang.code);
											setIsMobileMenuOpen(false);
										}}
										className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer border overflow-hidden p-0.5 ${
											currentLanguage === lang.code
												? 'bg-brand-forest border-brand-gold shadow-xs scale-105 ring-1 ring-brand-gold/60 z-10'
												: 'border-earth-sand/30 hover:bg-earth-sand/20 text-brand-charcoal opacity-80'
										}`}
										title={lang.name}
										aria-label={lang.name}
									>
										<CountryFlag code={lang.code} className='w-5 h-3.5 rounded-xs object-cover shadow-2xs' />
									</button>
								))}
							</div>
						</div>

						<div className='pt-2 border-t border-earth-sand/20'>
							<button
								onClick={() => {
									setActiveTab('builder');
									setIsMobileMenuOpen(false);
								}}
								className='w-full bg-brand-forest hover:bg-brand-forest-light text-earth-cream font-sans text-xs uppercase tracking-widest font-bold py-3 text-center rounded-lg'
							>
								{t('requestAQuote')}
							</button>
						</div>
					</div>
				)}
			</nav>

			{/* Main Content Area: If on discover tab, we let content bleed underneath since navbar is transparent */}
			<main className={`flex-1 transition-all duration-300 ${activeTab === 'discover' ? 'pt-0' : 'pt-24 pb-16'}`}>
				{children}
			</main>

			{/* 2. MODERN, MINIMAL FOOTER WITH SUSTAINABILITY FOCUS */}
			<footer
				id='layout-footer'
				className='bg-brand-forest-dark text-earth-cream border-t border-brand-forest/20 relative pt-16 pb-12 overflow-hidden z-10'
			>
				{/* Subtle Luxury African Watermark Silhouette Background */}
				<div className='absolute right-0 bottom-0 opacity-5 pointer-events-none w-96 h-96 select-none translate-x-12 translate-y-12'>
					<svg viewBox='0 0 100 100' fill='currentColor' className='text-white w-full h-full'>
						<path d='M45,45 C55,30 70,30 80,45 C85,55 80,75 65,85 C55,90 40,85 35,75 C25,65 30,50 45,45 Z' />
						<path d='M10,80 L90,80 C90,80 80,75 50,75 C20,75 10,80 10,80 Z' />
					</svg>
				</div>

				<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
					<div className='grid grid-cols-1 md:grid-cols-12 gap-8 pb-3 border-b border-earth-sand/15'>
						{/* COLUMN 1: Sustainability-first core narrative */}
						<div className='md:col-span-5 space-y-5 text-left'>
							<div className='space-y-1'>
								<span className='font-serif text-lg text-white font-medium tracking-widest block leading-tight'>
									ELEPHANT EXPEDITIONS
								</span>
								<span className='font-mono text-[9px] text-brand-gold tracking-[0.2em] block uppercase'>
									{t('est2025Text')}
								</span>
							</div>

							<p className='font-sans text-xs text-earth-sand-light/90 leading-relaxed font-light pr-4'>
								{t('footerAboutText')}
							</p>
						</div>

						{/* COLUMN 2: Quick navigation links */}
						<div className='md:col-span-3 space-y-4 text-left'></div>

						{/* COLUMN 3: Social & Contact */}
						<div className='md:col-span-4 space-y-4 text-left'>
							{/* Social Media & Contact Links */}
							<div className='flex flex-col space-y-3 pt-3'>
								<div className='flex items-center space-x-3.5'>
									<span className='font-sans text-[10px] uppercase tracking-wider text-brand-gold font-bold'>
										{t('followOurJourneysLabel')}
									</span>
									<div className='flex items-center space-x-2.5'>
										<a
											href='https://www.instagram.com/elephant_expeditions/profilecard/'
											target='_blank'
											rel='noopener noreferrer'
											className='p-1.5 rounded-full bg-white/5 border border-white/10 text-earth-sand-light hover:text-brand-gold hover:bg-white/10 hover:border-brand-gold/45 transition-all duration-200'
											title='Instagram'
										>
											<InstagramIcon className='w-4 h-4' />
										</a>
										<a
											href='https://www.facebook.com/people/Elephant-Expeditions/61571970292947/'
											target='_blank'
											rel='noopener noreferrer'
											className='p-1.5 rounded-full bg-white/5 border border-white/10 text-earth-sand-light hover:text-brand-gold hover:bg-white/10 hover:border-brand-gold/45 transition-all duration-200'
											title='Facebook'
										>
											<FacebookIcon className='w-4 h-4' />
										</a>
										<a
											href='mailto:info@elephant-expeditions.com'
											className='p-1.5 rounded-full bg-white/5 border border-white/10 text-earth-sand-light hover:text-brand-gold hover:bg-white/10 hover:border-brand-gold/45 transition-all duration-200'
											title='Email info@elephant-expeditions.com'
										>
											<Mail className='w-4 h-4' />
										</a>
									</div>
								</div>

								<div>
									<a
										href='mailto:info@elephant-expeditions.com'
										className='inline-flex items-center space-x-2 text-xs font-sans text-earth-sand-light/90 hover:text-brand-gold transition-colors duration-200'
									>
										<Mail className='w-3.5 h-3.5 text-brand-gold shrink-0' />
										<span className='tracking-wide'>info@elephant-expeditions.com</span>
									</a>
								</div>
							</div>
						</div>
					</div>

					{/* Environmental commitment footer sub-bar */}
					<div className='pt-8 flex flex-col md:flex-row items-center justify-between gap-4 font-sans text-[10.5px] text-earth-sand-light font-light text-center'>
						<div className='space-y-1 md:text-left'>
							<div>
								© {new Date().getFullYear()} Elephant Expeditions Ltd. All Rights Reserved. {t('fullyCertifiedASTA')}
							</div>
							{/* <div className='text-[9.5px] text-brand-gold-light/60'>{t('registeredConcessionsText')}</div> */}
						</div>
						<div className='flex flex-wrap justify-center gap-x-4 gap-y-1.5'>
							<button
								type='button'
								onClick={() => onOpenLegal?.('terms')}
								className='hover:text-brand-gold cursor-pointer transition-colors focus:outline-none'
							>
								{t('termsAndConditions')}
							</button>
							<span>•</span>
							<button
								type='button'
								onClick={() => onOpenLegal?.('privacy')}
								className='hover:text-brand-gold cursor-pointer transition-colors focus:outline-none'
							>
								{t('privacyPolicy')}
							</button>
							<span>•</span>
							<button
								type='button'
								onClick={() => onOpenLegal?.('indemnity')}
								className='hover:text-brand-gold cursor-pointer transition-colors focus:outline-none'
							>
								{t('indemnity')}
							</button>
						</div>
					</div>

					<div className='pt-6 text-center font-sans text-[10.5px] text-earth-sand-light/70 font-light'>
						<a
							href='https://www.goskyops.com'
							target='_blank'
							rel='noopener noreferrer'
							className='hover:opacity-90 transition-opacity duration-200'
						>
							Powered by{' '}
							<span className='text-brand-gold underline underline-offset-2 font-medium'>
								SkyOps Digital
							</span>
						</a>
					</div>
				</div>
			</footer>

			{/* Floating Interactive WhatsApp Support Widget */}
			<WhatsAppWidget />
		</div>
	);
}
